import type { FitHistoryEntry, SavedMatch } from '@/types/history';

const HISTORY_KEY = 'fitmap:history';
const SAVED_MATCHES_KEY = 'fitmap:saved-matches';
const MAX_HISTORY = 20;

function readList<T>(key: string): T[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function writeList<T>(key: string, list: T[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(list));
  } catch {
    // Storage unavailable — nothing persists this session, but the app keeps working.
  }
}

export function getHistory(): FitHistoryEntry[] {
  return readList<FitHistoryEntry>(HISTORY_KEY);
}

/** Newest first, capped at MAX_HISTORY — this is an activity trail, not permanent storage. */
export function addHistoryEntry(entry: FitHistoryEntry): FitHistoryEntry[] {
  const next = [entry, ...readList<FitHistoryEntry>(HISTORY_KEY)].slice(0, MAX_HISTORY);
  writeList(HISTORY_KEY, next);
  return next;
}

export function removeHistoryEntry(id: string): FitHistoryEntry[] {
  const next = readList<FitHistoryEntry>(HISTORY_KEY).filter((e) => e.id !== id);
  writeList(HISTORY_KEY, next);
  return next;
}

export function clearHistory(): void {
  writeList(HISTORY_KEY, []);
}

export function getSavedMatches(): SavedMatch[] {
  return readList<SavedMatch>(SAVED_MATCHES_KEY);
}

/** Newest first — no cap; the user explicitly chose to save each of these. */
export function addSavedMatch(match: SavedMatch): SavedMatch[] {
  const next = [match, ...readList<SavedMatch>(SAVED_MATCHES_KEY)];
  writeList(SAVED_MATCHES_KEY, next);
  return next;
}

export function removeSavedMatch(id: string): SavedMatch[] {
  const next = readList<SavedMatch>(SAVED_MATCHES_KEY).filter((m) => m.id !== id);
  writeList(SAVED_MATCHES_KEY, next);
  return next;
}
